import { Table, TableHead, TableRow, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { useState, useRef, useEffect, useContext } from "react";
import five from "../../../src/assets/images/five.png";
import four from "../../../src/assets/images/four.png";
import one from "../../../src/assets/images/one.png";
import three from "../../../src/assets/images/three.png";
import two from "../../../src/assets/images/two.png";

import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import { useReactToPrint } from "react-to-print";
import Modal from "../Modal/Modal";
import MUITable from "./../../Component/UI/Table/Table";
import SentimentItem from "./SentimentItem";
import classes from "./SentimentTable.module.css";
import { getCleanQuestion } from "../../utils/helpers";
import { getColorForScore } from "../../utils/helpers";
import { DiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { downloadExcel } from "../../utils/monitoring-helpers";
import BaseResponsesModal from "./BaseResponsesModal";
import useBaseResponsesModal from "./useBaseResponsesModal";

export function trimBeforeInstructions(text) {
  const pattern = /instructions\s*:\s*1\s*\.\s*create\s*a\s*single\s*paragraph\s*of\s*exactly/i;
  const match = text.match(pattern);

  if (match) {
    const index = match.index;
    return text.substring(0, index).trim();
  }

  return text;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

// Paginated component for reporting mode
function PaginatedSentimentTable({
  data,
  renderRow,
  renderHeader,
  maxPageHeight = 680
}) {
  const [paginatedData, setPaginatedData] = useState([]);
  const [isCalculating, setIsCalculating] = useState(true);
  const measurementRef = useRef(null);

  const sentiments = data?.sentimentMeta || [];

  useEffect(() => {
    if (sentiments.length === 0) {
      setIsCalculating(false);
      return;
    }

    setTimeout(() => {
      calculatePages();
    }, 100);
  }, [sentiments]);

  const calculatePages = () => {
    if (!measurementRef.current) return;

    const rows = measurementRef.current.querySelectorAll('tbody tr');
    const headerHeight = measurementRef.current.querySelector('thead')?.offsetHeight || 60;

    const pages = [];
    let currentPage = [];
    let currentHeight = headerHeight;

    rows.forEach((row, index) => {
      const rowHeight = row.offsetHeight;

      // Increase safety margin
      const safetyMargin = 40; // Increased from 20 to 40

      if (currentHeight + rowHeight + safetyMargin > maxPageHeight && currentPage.length > 0) {
        pages.push([...currentPage]);
        currentPage = [index];
        currentHeight = headerHeight + rowHeight;
      } else {
        currentPage.push(index);
        currentHeight += rowHeight;
      }
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    setPaginatedData(pages);
    setIsCalculating(false);
  };

  const renderMeasurementTable = () => (
    <div
      ref={measurementRef}
      style={{
        position: 'absolute',
        visibility: 'hidden',
        width: '1100px',
        padding: '16px'
      }}
    >
      <MUITable
        size="medium"
        elevation={0}
        data={sentiments}
        render={renderRow}
        renderHeaders={() => renderHeader(sentiments)}
      />
    </div>
  );

  const renderPageTable = (pageIndices, pageNumber, totalPages) => {
    const pageData = pageIndices.map(index => sentiments[index]);

    return (
      <div
        key={pageNumber}
        className="sentiment-page"
        style={{
          pageBreakAfter: pageNumber < totalPages - 1 ? 'always' : 'auto',
          marginBottom: pageNumber < totalPages - 1 ? '30px' : '0',
          width: '1100px',
          minHeight: `${maxPageHeight}px`,  // 👈 ADD THIS - ensures consistent height
          maxHeight: `${maxPageHeight}px`,
          border: '1px solid #e0e0e0',
          padding: '16px',
          borderRadius: '4px',
          backgroundColor: '#fff'
        }}
      >
        {/* {totalPages > 1 && (
          <div style={{
            textAlign: 'right',
            fontSize: '12px',
            color: '#666',
            marginBottom: '8px',
            fontWeight: 'bold'
          }}>
            Page {pageNumber + 1} of {totalPages}
          </div>
        )} */}

        <MUITable
          size="medium"
          elevation={0}
          data={pageData}
          render={(item, localIndex) => {
            const globalIndex = pageIndices[localIndex];
            return renderRow(item, globalIndex);
          }}
          renderHeaders={() => renderHeader(sentiments)}
        />
      </div>
    );
  };

  if (isCalculating) {
    return (
      <>
        {renderMeasurementTable()}
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Typography variant="body1">Calculating page layout...</Typography>
        </div>
      </>
    );
  }

  if (paginatedData.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No data available
      </Typography>
    );
  }

  return (
    <div className="paginated-sentiment-tables">
      {/* <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        This view is optimized for PPT export. Total pages: {paginatedData.length}
      </Typography> */}
      {paginatedData.map((pageIndices, pageIndex) =>
        renderPageTable(pageIndices, pageIndex, paginatedData.length)
      )}
    </div>
  );
}

export default function SentimentTable({
  data,
  showScore = true,
  selectedInput,
  fromDashboardBrandScore = false,
  compIndexScoreMetaData = [],
  brandProduct,
  showInfoIcon = false
}) {
  // Read isReporting flag from URL parameters
  const isReporting = new URLSearchParams(window.location.search).get('isReporting') === 'true' || new URLSearchParams(window.location.search).get('reporting') === 'true';
  console.log("isReporting:", isReporting);

  const [showSentimentModal, setShowSentimentModal] = useState(false);
  const [selectedSentiment, setSelectedSentiment] = useState({
    data: null,
    questionNumber: 0,
    datasetNumber: 0,
  });
  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [selectedSources, setSelectedSources] = useState({
    question: "",
    sources: [],
    number: 0,
  });

  // New state for reporting modal
  const [showReportingModal, setShowReportingModal] = useState(false);
    const {
    infoModalOpen: sentimentInfoModalOpen,
    infoModalTitle: sentimentInfoModalTitle,
    infoModalContent: sentimentInfoModalContent,
    closeInfoModal: closeSentimentInfoModal,
    handleSentimentQuestionClick,
    handleSentimentAnalysisClick,
  } = useBaseResponsesModal(data, "sentiment");


  const sentiments = data?.sentimentMeta || [];

  const discoveryCtx = useContext(DiscoveryContext);
  const isPropensity = discoveryCtx?.isPropensityToBuy?.() ?? (discoveryCtx?.brand_scores?.rbiType === "propensityToBuy");
  const mapTitle = (t) => discoveryCtx?.mapMetricTitle?.(t) ?? t;

  function getSentimentIcon(llm_sentiment) {
    let sentimentIcon = "";
    switch (llm_sentiment) {
      case "Positive":
      case "Highly Likely":
        sentimentIcon = <img height={30} width={30} src={five} alt="5" />;
        break;
      case "Slightly Positive":
      case "Likely":
        sentimentIcon = <img height={30} width={30} src={four} alt="4" />;
        break;
      case "Neutral":
        sentimentIcon = <img height={30} width={30} src={three} alt="3" />;
        break;
      case "Unlikely":
      case "Slightly Negative":
        sentimentIcon = <img height={30} width={30} src={two} alt="2" />;
        break;
      case "Very Unlikely":
      case "Negative":
        sentimentIcon = <img height={30} width={30} src={one} alt="1" />;
        break;
      default:
        sentimentIcon = "";
    }

    return sentimentIcon;
  }

  function renderRow(item, index) {
    const handleQuestionClick = () => {
      let aggregatedSources = [
        ...new Set(
          item.flatMap((itemSentiment) => itemSentiment.sources || [])
        ),
      ];

      aggregatedSources =
        aggregatedSources?.filter((source) => !source.includes("vertexaisearch")) || [];

      if (selectedInput?.llm?.toLowerCase().includes("search")) {
        setShowSourcesModal(true);
      }
      setSelectedSources({
        number: index + 1,
        question: getCleanQuestion(item[0]?.question),
        sources: aggregatedSources,
      });
    };

    return (
      <TableRow
        key={index}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell style={{ width: "400px" }} component="th" scope="row">
          <Typography
            variant="body2"
            sx={{ cursor: "pointer" }}
            onClick={handleQuestionClick}
          >
            {Array.isArray(item) && item.length
              ? `${index + 1}. ${getCleanQuestion(item[0].question)}`
              : index + 1}
          </Typography>
        </TableCell>

        {item.map((itemSentiment, idx) => {
          return (
            <TableCell align="center" key={idx}>
              <span
                className={classes.sentiment_emoji}
                onClick={() => {
                  if (itemSentiment.llm_sentiment) {
                    setShowSentimentModal(idx + 1);
                    setSelectedSentiment({
                      questionNumber: index + 1,
                      datasetNumber: idx + 1,
                      data: itemSentiment,
                    });
                  }
                }}
              >
                {itemSentiment?.sentiment !== undefined &&
                  itemSentiment?.sentiment != null
                  ? (isPropensity
                    ? `${mapTitle("Sentiment")}: ${itemSentiment?.sentiment}`
                    : `Sentiment: ${itemSentiment?.sentiment}`)
                  : getSentimentIcon(itemSentiment.llm_sentiment)}
              </span>
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  // Simplified renderRow for reporting modal (no click handlers)
  function renderRowForReporting(item, index) {
    return (
      <TableRow
        key={index}
        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
      >
        <TableCell style={{ width: "400px" }} component="th" scope="row">
          <Typography variant="body2">
            {Array.isArray(item) && item.length
              ? `${index + 1}. ${getCleanQuestion(item[0].question)}`
              : index + 1}
          </Typography>
        </TableCell>

        {item.map((itemSentiment, idx) => {
          return (
            <TableCell align="center" key={idx}>
              <span className={classes.sentiment_emoji}>
                {itemSentiment?.sentiment !== undefined &&
                  itemSentiment?.sentiment != null
                  ? (isPropensity ? `${mapTitle("Sentiment")}: ${itemSentiment?.sentiment}` : `Sentiment: ${itemSentiment?.sentiment}`)
                  : getSentimentIcon(itemSentiment.llm_sentiment)}
              </span>
            </TableCell>
          );
        })}
      </TableRow>
    );
  }

  function renderHeader(sentiments) {
    return (
      <TableHead className={classes.table_header}>
        <TableRow>
          <TableCell>Questions</TableCell>
          {sentiments.length &&
            sentiments[0].map((_, i) => (
              <TableCell align="center" key={i}>Dataset {i + 1}</TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  }

  const pdfRef = useRef();

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    documentTitle: `${selectedInput?.brand || selectedInput?.product || 'brand'}_sentiment_questions.pdf`,
    removeAfterPrint: true,
    pageStyle: `
            @page {
              size: 380mm 250mm;
              margin-left: 10mm;
              margin-right: 10mm;
              margin-bottom: 5mm;
              margin-top: 15mm;
            }
            @media print {
            .parent-container {
        overflow: hidden;
      }
              body {
                -webkit-print-color-adjust: exact;
              }
              .page-footer {
                position: fixed;
                bottom: 10mm;
                left: 10mm;
                right: 10mm;
                font-size: 20pt;
                color: #777;
                text-align: center;
              }
            }
          `,
  });

  function renderSentimentScores(sentiment) {
    if (showScore && sentiment?.length > 0) {
      // Prepare all rows: main brand + competitors (if fromDashboardBrandScore)
      const allRows = [];
      const competitorRows = []; // Track competitor rows separately
      const addedRowNames = new Set(); // Track added row names to prevent duplicates

      // Add main brand row
      if (data?.favorabilities && data?.favorabilities.length > 0) {
        data.favorabilities.forEach((fav) => {
          if (fav?.name && !addedRowNames.has(fav.name)) {
            allRows.push(fav);
            addedRowNames.add(fav.name);
          }
        });
      }

      // Add competitor rows if from Dashboard BrandScore
      if (fromDashboardBrandScore && compIndexScoreMetaData?.length > 0) {
        compIndexScoreMetaData.forEach((compMeta) => {
          // Skip if it's the main brand
          if (compMeta?.name === brandProduct) return;

          const compData = compMeta?.extras || compMeta;
          const compFavorabilities = compData?.favorabilities || [];

          if (compFavorabilities.length > 0) {
            compFavorabilities.forEach((fav) => {
              if (fav?.name && !addedRowNames.has(fav.name)) {
                allRows.push(fav);
                addedRowNames.add(fav.name);
                competitorRows.push(fav);
              }
            });
          }
        });


        // Calculate Competitor Average row (excluding brand scores)
        if (competitorRows.length > 0) {
          const competitorAverageRow = { name: "Competitor Average" };

          // Get number of sentiment columns
          const sentimentKeys = Object.keys(competitorRows[0]).filter(key => key.startsWith("sentiment"));

          // Calculate average for each sentiment column (competitors only)
          sentimentKeys.forEach((key, index) => {
            const sum = competitorRows.reduce((acc, row) => {
              const value = parseFloat(row[key]);
              return acc + (isNaN(value) ? 0 : value);
            }, 0);
            const avg = competitorRows.length > 0 ? sum / competitorRows.length : 0;
            competitorAverageRow[key] = avg.toFixed(2);
          });

          // Calculate average of the Avg column (competitors only)
          const avgSum = competitorRows.reduce((acc, row) => {
            const value = parseFloat(row.average);
            return acc + (isNaN(value) ? 0 : value);
          }, 0);
          competitorAverageRow.average = (competitorRows.length > 0 ? avgSum / competitorRows.length : 0).toFixed(2);

          // Calculate average of score column if it exists (competitors only)
          if (competitorRows[0].score !== undefined) {
            const scoreSum = competitorRows.reduce((acc, row) => {
              const value = parseFloat(row.score);
              return acc + (isNaN(value) ? 0 : value);
            }, 0);
            competitorAverageRow.score = (competitorRows.length > 0 ? scoreSum / competitorRows.length : 0).toFixed(2);
          }

          // Add Competitor Average row at the end
          allRows.push(competitorAverageRow);
        }
      }


      // Prepare data for color coding
      const sentimentColumnCount = data?.favorabilities?.[0]
        ? Object.keys(data?.favorabilities[0]).filter(key => key.startsWith("sentiment")).length
        : 0;
      
      const isExclude = true; // Set to false to include Competitor Average in color comparison
      
      // Filter rows for color comparison based on isExclude flag
      const rowsForColorComparison = isExclude 
        ? allRows.filter(row => row.name !== "Competitor Average")
        : allRows;
      
      // Get all values for each sentiment column for color comparison
      const sentimentColumnValues = {};
      for (let i = 0; i < sentimentColumnCount; i++) {
        const colKey = `sentiment${i + 1}`;
        sentimentColumnValues[colKey] = rowsForColorComparison
          .map(row => row[colKey])
          .filter(val => val !== undefined && val !== null);
      }

      // Get all average values for color comparison
      const averageValues = rowsForColorComparison
        .map(row => row.average)
        .filter(val => val !== undefined && val !== null);

      return (
        <>
          <hr />
          <h6>B. {isPropensity ? mapTitle("Average Sentiment Scores") : "Average Sentiment Scores"}</h6>
          <hr />
          <Table sx={{ minWidth: 800 }} aria-label="simple table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Name</StyledTableCell>
                {data?.favorabilities &&
                  data?.favorabilities.length > 0 &&
                  Array.from({
                    length: Object.keys(data?.favorabilities?.[0])?.filter((key) =>
                      key.startsWith("sentiment")
                    ).length,
                  }).map((_, i) => (
                    <StyledTableCell align="right" key={i}>
                      Question {i + 1}
                    </StyledTableCell>
                  ))}
                <StyledTableCell align="right">Avg</StyledTableCell>
                {data?.favorabilities &&
                  data?.favorabilities.length > 0 &&
                  Object.keys(data?.favorabilities[0]).includes("score") && (
                    <StyledTableCell>Score</StyledTableCell>
                  )}
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {allRows?.map((row, idx) => {
                const isCompetitorAverage = row.name === "Competitor Average";
                return (
                  <StyledTableRow
                    key={`${row.name}-${idx}`}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      backgroundColor: isCompetitorAverage ? "#e3f2fd" : "inherit",
                      fontWeight: isCompetitorAverage ? "bold" : "normal"
                    }}
                  >
                    <StyledTableCell
                      component="th"
                      scope="row"
                      sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                    >
                      {row.name}
                    </StyledTableCell>
                    {data?.favorabilities &&
                      data?.favorabilities.length > 0 &&
                      Array.from({
                        length: Object.keys(data?.favorabilities?.[0])?.filter(
                          (key) => key.startsWith("sentiment")
                        ).length,
                      })?.map((_, i) => {
                        const colKey = `sentiment${i + 1}`;
                        const cellValue = row[colKey];
                        const cellColor = (!isCompetitorAverage || !isExclude) 
                          ? getColorForScore(cellValue, sentimentColumnValues[colKey]) 
                          : 'inherit';
                        return (
                          <StyledTableCell
                            align="right"
                            key={i}
                            sx={{
                              fontWeight: isCompetitorAverage ? "bold" : "normal",
                              color: cellColor
                            }}
                          >
                            {cellValue}
                          </StyledTableCell>
                        );
                      })}
                    <StyledTableCell
                      align="right"
                      sx={{
                        fontWeight: isCompetitorAverage ? "bold" : "normal",
                        color: (!isCompetitorAverage || !isExclude) 
                          ? getColorForScore(row.average, averageValues) 
                          : 'inherit'
                      }}
                    >
                      {row.average}
                    </StyledTableCell>
                    {row.score && (
                      <StyledTableCell
                        align="right"
                        sx={{ fontWeight: isCompetitorAverage ? "bold" : "normal" }}
                      >
                        {row?.score}
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      );
    }
  }

  return (
    <>
      <div id="sentiment_id">
        {showScore && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h6>A. {isPropensity ? mapTitle("Sentiment Analysis") : "Sentiment Analysis"}</h6>
              <div style={{ display: "flex", gap: "12px" }}>
                <DownloadIcon onClick={exportToPDF} style={{ cursor: "pointer" }} />
              </div>
            </div>
            <hr />
          </>
        )}

        {isReporting && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => setShowReportingModal(true)}
              style={{ textTransform: "none" }}
            >
              Report View
            </Button>
          </div>
        )}

        <div style={{ display: "flex" }} id="print-pdf" ref={pdfRef}>
          <MUITable
            size="medium"
            elevation={0}
            data={sentiments}
            render={renderRow}
            renderHeaders={() => renderHeader(sentiments)}
          />
        </div>
      </div>

      {/* Reporting Modal - Paginated View */}
      <Modal
        handleClose={() => setShowReportingModal(false)}
        open={showReportingModal}
        title={`${mapTitle("Sentiment Analysis")} - Report View (Paginated for PPT)`}
        width={1200}
      // height={800}
      >
        <div style={{ maxHeight: "70vh", overflowY: "auto", padding: "8px" }}>
          <PaginatedSentimentTable
            data={data}
            renderRow={renderRowForReporting}
            renderHeader={renderHeader}
            maxPageHeight={600}
          />
        </div>
      </Modal>

      {/* Sources Modal */}
      <Modal
        handleClose={() => setShowSourcesModal(false)}
        open={showSourcesModal && selectedInput?.llm?.toLowerCase().includes("search")}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <span>Data Sources (Aggregated)</span>
            &nbsp; &nbsp;
            <DownloadIcon
              style={{ cursor: "pointer" }}
              onClick={() =>
                downloadExcel(
                  selectedSources?.sources,
                  "Sentiment Questions",
                  selectedInput,
                  selectedSources?.question
                )
              }
            />
          </div>
        }
        width={850}
      >
        {selectedSources?.sources?.length ? (
          <div style={{ maxHeight: "400px", overflowY: "auto", padding: "8px" }}>
            <ul>
              {selectedSources.sources.map((src, i) => (
                <li key={i}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, wordBreak: "break-word" }}
                  >
                    <a href={src} target="_blank" rel="noopener noreferrer">
                      {src}
                    </a>
                  </Typography>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No sources available.
          </Typography>
        )}
      </Modal>

      {/* Sentiment Modal */}
     <Modal
        handleClose={() => setShowSentimentModal(false)}
        open={showSentimentModal}
        title={`${mapTitle("Sentiment")}: Question ${selectedSentiment?.questionNumber} (Dataset ${selectedSentiment?.datasetNumber})`}
        width={850}
      >
        <SentimentItem 
          selectedInput={selectedInput} 
          sentiment={selectedSentiment?.data}
          showInfoIcons={showInfoIcon}
          onQuestionInfoClick={handleSentimentQuestionClick}
          onExplanationInfoClick={handleSentimentAnalysisClick}
        />
      </Modal>
       <BaseResponsesModal
        open={sentimentInfoModalOpen}
        title={sentimentInfoModalTitle}
        content={sentimentInfoModalContent}
        onClose={closeSentimentInfoModal}
      />

      {showScore && renderSentimentScores(sentiments)}
    </>
  );
}